import { Stack } from '@mui/material'
import { C1Icon } from 'components/core/icons/C1Icon'
import {
    getAccessReviewStyleSelection,
    setAccessReviewStyleSelection,
} from 'components/pages/my_tasks/access_reviews/common/review-style-picker/review-style-local-storage'
import { ReviewStyleBox } from 'components/pages/my_tasks/access_reviews/common/review-style-picker/ReviewStyleBox'
import { routes } from 'lib/routes'
import { CircleUser, List, Shapes } from 'lucide-react'
import { AccessReviewViewType } from 'pbts/c1/api/accessreview/v1/accessreview'
import { FC, useState } from 'react'
import { useIntl } from 'react-intl'

export type ReviewStylePickerBodyProps = {
    recommendedStyle?: AccessReviewViewType
    accessReviewID?: string
    onChange?: () => void
}

export const ReviewStylePickerBody: FC<ReviewStylePickerBodyProps> = ({ recommendedStyle, accessReviewID, onChange }) => {
    const intl = useIntl()
    const [selected, setSelected] = useState<AccessReviewViewType>(getAccessReviewStyleSelection(accessReviewID))

    return (
        <Stack direction="row" spacing={2} justifyContent="center">
            <ReviewStyleBox
                title={intl.formatMessage({
                    id: 'review_style_picker_by_app_title',
                    defaultMessage: 'By application',
                })}
                subtitle={intl.formatMessage({
                    id: 'review_style_picker_by_app_subtitle',
                    defaultMessage: 'Best choice for app owners',
                })}
                description={intl.formatMessage({
                    id: 'review_style_picker_by_app_description',
                    defaultMessage: 'View your access reviews sorted by application.',
                })}
                selected={selected === AccessReviewViewType.BY_APP}
                recommended={recommendedStyle === AccessReviewViewType.BY_APP}
                onClick={(): void => {
                    setSelected(AccessReviewViewType.BY_APP)
                    setAccessReviewStyleSelection(accessReviewID || '', AccessReviewViewType.BY_APP)
                    if (onChange) {
                        onChange()
                    }
                }}
                href={routes.assignments.reviews.byApp(accessReviewID || '')}
                icon={<C1Icon icon={Shapes} size="lg" />}
            />
            <ReviewStyleBox
                title={intl.formatMessage({
                    id: 'review_style_picker_by_user_title',
                    defaultMessage: 'By user',
                })}
                subtitle={intl.formatMessage({
                    id: 'review_style_picker_by_user_subtitle',
                    defaultMessage: 'Best choice for managers',
                })}
                description={intl.formatMessage({
                    id: 'review_style_picker_by_user_description',
                    defaultMessage: 'View your access reviews sorted by the human user to whom the access belongs.',
                })}
                selected={selected === AccessReviewViewType.BY_USER}
                recommended={recommendedStyle === AccessReviewViewType.BY_USER}
                onClick={(): void => {
                    setSelected(AccessReviewViewType.BY_USER)
                    setAccessReviewStyleSelection(accessReviewID || '', AccessReviewViewType.BY_USER)
                    if (onChange) {
                        onChange()
                    }
                }}
                href={routes.assignments.reviews.byUser(accessReviewID || '')}
                icon={<C1Icon icon={CircleUser} size="lg" />}
            />
            <ReviewStyleBox
                title={intl.formatMessage({
                    id: 'review_style_picker_unstructured_title',
                    defaultMessage: 'Unstructured',
                })}
                subtitle={intl.formatMessage({
                    id: 'review_style_picker_unstructured_subtitle',
                    defaultMessage: 'Forge your own path',
                })}
                description={intl.formatMessage({
                    id: 'review_style_picker_unstructured_description',
                    defaultMessage: 'Sort and filter your access reviews however you like.',
                })}
                selected={selected === AccessReviewViewType.UNSTRUCTURED}
                recommended={recommendedStyle === AccessReviewViewType.UNSTRUCTURED}
                onClick={(): void => {
                    setSelected(AccessReviewViewType.UNSTRUCTURED)
                    setAccessReviewStyleSelection(accessReviewID || '', AccessReviewViewType.UNSTRUCTURED)
                    if (onChange) {
                        onChange()
                    }
                }}
                href={routes.assignments.reviews.unstructured(accessReviewID || '')}
                icon={<C1Icon icon={List} size="lg" />}
            />
        </Stack>
    )
}
